<script>
import { ref, provide } from 'vue';
// composable
import { useAlert } from 'dashboard/composables';
import { useConfig } from 'dashboard/composables/useConfig';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';

// components
import ReplyBox from './ReplyBox.vue';
import MessageList from 'next/message/MessageList.vue';
import Banner from 'dashboard/components/ui/Banner.vue';
import ConversationVoiceCallsBanner from 'dashboard/components/voice/ConversationVoiceCallsBanner.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ConfirmActionDialog from 'dashboard/components-next/dialog/ConfirmActionDialog.vue';

// stores and apis
import { mapGetters } from 'vuex';

// mixins
import inboxMixin, { INBOX_FEATURES } from 'shared/mixins/inboxMixin';

// utils
import { emitter } from 'shared/helpers/mitt';
import { getTypingUsersText } from '../../../helper/commons';
import { calculateScrollTop } from './helpers/scrollTopCalculationHelper';
import { LocalStorage } from 'shared/helpers/localStorage';
import {
  filterDuplicateSourceMessages,
  getReadMessages,
  getUnreadMessages,
} from 'dashboard/helper/conversationHelper';

// constants
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { REPLY_POLICY } from 'shared/constants/links';
import wootConstants from 'dashboard/constants/globals';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { MESSAGE_TYPE } from 'shared/constants/messages';

export default {
  components: {
    MessageList,
    ReplyBox,
    Banner,
    ConversationVoiceCallsBanner,
    Spinner,
    ConfirmActionDialog,
  },
  mixins: [inboxMixin],
  props: {
    isQaMonitor: {
      type: Boolean,
      default: false,
    },
    hideReplyBox: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const isPopOutReplyBox = ref(false);
    const conversationPanelRef = ref(null);
    const { isEnterprise } = useConfig();

    const keyboardEvents = {
      Escape: {
        action: () => {
          isPopOutReplyBox.value = false;
        },
      },
    };

    useKeyboardEvents(keyboardEvents);

    provide('contextMenuElementTarget', conversationPanelRef);

    return {
      isEnterprise,
      isPopOutReplyBox,
      conversationPanelRef,
    };
  },
  data() {
    return {
      isLoadingPrevious: true,
      heightBeforeLoad: null,
      conversationPanel: null,
      hasUserScrolled: false,
      isProgrammaticScroll: false,
      messageSentSinceOpened: false,
      isAssigningToSelf: false,
      isClosingUnassignedChat: false,
    };
  },

  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUserId: 'getCurrentUserID',
      listLoadingStatus: 'getAllMessagesLoaded',
      currentAccountId: 'getCurrentAccountId',
    }),
    isOpen() {
      return this.currentChat?.status === wootConstants.STATUS_TYPE.OPEN;
    },
    isResolved() {
      return this.currentChat?.status === wootConstants.STATUS_TYPE.RESOLVED;
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    typingUsersList() {
      const userList = this.$store.getters[
        'conversationTypingStatus/getUserList'
      ](this.currentChat.id);
      return userList;
    },
    isLiveChatWebWidget() {
      return this.isAWebWidgetInbox;
    },
    typingUsersToMonitor() {
      // Only for LiveChat(WebWidget): show who is typing + their draft preview if present.
      if (!this.isLiveChatWebWidget) return [];
      const list = this.typingUsersList || [];
      const currentUserId = Number(this.currentUserId);

      return list
        .filter(u => {
          const type = String(u.type || '').toLowerCase();
          const isAgent = type === 'user' || type === 'agent';
          if (!isAgent) return false;
          return Number(u.id) !== currentUserId;
        })
        .map(u => ({
          id: u.id,
          type: u.type,
          name: u.name || u.available_name || u.email || 'Agent',
          typingPreview: String(
            u.typingPreview || u.typing_preview || ''
          ).trim(),
        }));
    },
    isAnyoneTyping() {
      const userList = this.typingUsersList;
      return userList.length !== 0;
    },
    typingUserNames() {
      const userList = this.typingUsersList;
      if (this.isAnyoneTyping) {
        const [i18nKey, params] = getTypingUsersText(userList);
        return this.$t(i18nKey, params);
      }

      return '';
    },
    getMessages() {
      const messages = this.currentChat.messages || [];
      const baseList = this.isAWhatsAppChannel
        ? filterDuplicateSourceMessages(messages)
        : messages;

      // Per-window missed-chat view (Archives) — when the route carries
      // missed_window_start / missed_window_end, narrow the displayed messages to
      // that exact window so admins can see what the customer sent during the
      // missed period without scrolling through the entire history. The Archive
      // shell renders a banner with a "View full conversation" button that drops
      // these params and brings the full transcript back.
      const windowFiltered = this.applyMissedWindowFilter(baseList);
      return windowFiltered;
    },
    missedWindowRange() {
      const startRaw = this.$route?.query?.missed_window_start;
      const endRaw = this.$route?.query?.missed_window_end;
      if (startRaw == null || endRaw == null) return null;

      const start = Number(startRaw);
      const end = Number(endRaw);
      if (!Number.isFinite(start) || !Number.isFinite(end) || end <= 0) {
        return null;
      }
      return { start, end };
    },
    readMessages() {
      return getReadMessages(
        this.getMessages,
        this.currentChat.agent_last_seen_at
      );
    },
    unReadMessages() {
      return getUnreadMessages(
        this.getMessages,
        this.currentChat.agent_last_seen_at
      );
    },
    shouldShowSpinner() {
      return (
        (this.currentChat && this.currentChat.dataFetched === undefined) ||
        (!this.listLoadingStatus && this.isLoadingPrevious)
      );
    },
    // Check there is a instagram inbox exists with the same instagram_id
    hasDuplicateInstagramInbox() {
      const instagramId = this.inbox.instagram_id;
      const { additional_attributes: additionalAttributes = {} } = this.inbox;
      const instagramInbox =
        this.$store.getters['inboxes/getInstagramInboxByInstagramId'](
          instagramId
        );

      return (
        this.inbox.channel_type === INBOX_TYPES.FB &&
        additionalAttributes.type === 'instagram_direct_message' &&
        instagramInbox
      );
    },

    replyWindowBannerMessage() {
      if (this.isAWhatsAppChannel) {
        return this.$t('CONVERSATION.TWILIO_WHATSAPP_CAN_REPLY');
      }
      if (this.isAPIInbox) {
        const { additional_attributes: additionalAttributes = {} } = this.inbox;
        if (additionalAttributes) {
          const {
            agent_reply_time_window_message: agentReplyTimeWindowMessage,
            agent_reply_time_window: agentReplyTimeWindow,
          } = additionalAttributes;
          return (
            agentReplyTimeWindowMessage ||
            this.$t('CONVERSATION.API_HOURS_WINDOW', {
              hours: agentReplyTimeWindow,
            })
          );
        }
        return '';
      }
      return this.$t('CONVERSATION.CANNOT_REPLY');
    },
    replyWindowLink() {
      if (this.isAFacebookInbox || this.isAnInstagramChannel) {
        return REPLY_POLICY.FACEBOOK;
      }
      if (this.isAWhatsAppCloudChannel) {
        return REPLY_POLICY.WHATSAPP_CLOUD;
      }
      if (!this.isAPIInbox) {
        return REPLY_POLICY.TWILIO_WHATSAPP;
      }
      return '';
    },
    replyWindowLinkText() {
      if (
        this.isAWhatsAppChannel ||
        this.isAFacebookInbox ||
        this.isAnInstagramChannel
      ) {
        return this.$t('CONVERSATION.24_HOURS_WINDOW');
      }
      if (!this.isAPIInbox) {
        return this.$t('CONVERSATION.TWILIO_WHATSAPP_24_HOURS_WINDOW');
      }
      return '';
    },
    unreadMessageCount() {
      return this.currentChat.unread_count || 0;
    },
    unreadMessageLabel() {
      const count =
        this.unreadMessageCount > 9 ? '9+' : this.unreadMessageCount;
      const label =
        this.unreadMessageCount > 1
          ? 'CONVERSATION.UNREAD_MESSAGES'
          : 'CONVERSATION.UNREAD_MESSAGE';
      return `${count} ${this.$t(label)}`;
    },
    isQaMonitorRoute() {
      const routeName = String(this.$route?.name || '');
      const routePath = String(this.$route?.path || '');
      return (
        routeName.startsWith('qa_monitor') || routePath.includes('/qa-monitor')
      );
    },
    isQaMonitorMode() {
      return this.isQaMonitor || this.isQaMonitorRoute;
    },
    isSuperviseMode() {
      const supervise = this.$route?.query?.supervise;
      return supervise === '1' || supervise === 1 || supervise === true;
    },
    inboxSupportsReplyTo() {
      const incoming = this.inboxHasFeature(INBOX_FEATURES.REPLY_TO);
      const outgoing =
        this.inboxHasFeature(INBOX_FEATURES.REPLY_TO_OUTGOING) &&
        !this.is360DialogWhatsAppChannel;

      return { incoming, outgoing };
    },
    archivedMessage() {
      return 'This chat has been archived.';
    },
    currentAssignee() {
      return (
        this.currentChat?.meta?.assignee || this.currentChat?.assignee || null
      );
    },
    currentAssigneeId() {
      return Number(
        this.currentAssignee?.id || this.currentChat?.assignee_id || 0
      );
    },
    hasCurrentAssignee() {
      return this.currentAssigneeId > 0;
    },
    currentAssigneeName() {
      return this.currentAssignee?.name || '';
    },
    isAssignedToOtherAgent() {
      if (!this.hasCurrentAssignee) return false;
      return this.currentAssigneeId !== Number(this.currentUserId);
    },
    takeoverDescription() {
      if (!this.currentAssigneeName) return '';
      return `This chat is currently assigned to <strong>${this.currentAssigneeName}</strong>. Are you sure you want to take over?`;
    },
    closeChatDescription() {
      const contactName =
        this.currentChat?.meta?.sender?.name || 'the customer';
      return `Are you sure you want to close the chat with <strong>${contactName}</strong>?`;
    },
    hasFirstAgentReply() {
      if (this.currentChat?.first_reply_created_at) {
        return true;
      }

      const messages = this.currentChat?.messages || [];
      return messages.some(message => {
        return (
          Number(message?.message_type) === MESSAGE_TYPE.OUTGOING &&
          String(message?.sender_type || '') === 'User' &&
          message?.private !== true
        );
      });
    },
    shouldShowUnassignedReplyGate() {
      if (this.isQaMonitorMode || this.isSuperviseMode || this.isResolved) {
        return false;
      }

      if (!this.currentChat?.id) {
        return false;
      }

      return !this.hasCurrentAssignee;
    },
  },

  watch: {
    currentChat(newChat, oldChat) {
      if (newChat.id === oldChat.id) {
        return;
      }
      this.fetchAllAttachmentsFromCurrentChat();
      this.messageSentSinceOpened = false;
    },
  },

  created() {
    emitter.on(BUS_EVENTS.SCROLL_TO_MESSAGE, this.onScrollToMessage);
  },

  mounted() {
    this.addScrollListener();
    this.fetchAllAttachmentsFromCurrentChat();
  },

  unmounted() {
    this.removeBusListeners();
    this.removeScrollListener();
  },

  methods: {
    applyMissedWindowFilter(messages) {
      const range = this.missedWindowRange;
      if (!range) return messages;

      // window_start is inclusive, window_end is exclusive — matches how the
      // backend computes missed sessions (the next session_started or
      // conversation_resolved closes the previous window). Activity messages
      // that fall on either boundary are kept on the side that owns them.
      return messages.filter(message => {
        const ts = Number(message?.created_at || 0);
        if (!Number.isFinite(ts) || ts <= 0) return false;
        return ts >= range.start && ts <= range.end;
      });
    },
    fetchAllAttachmentsFromCurrentChat() {
      this.$store.dispatch('fetchAllAttachments', this.currentChat.id);
    },
    removeBusListeners() {
      emitter.off(BUS_EVENTS.SCROLL_TO_MESSAGE, this.onScrollToMessage);
    },
    onScrollToMessage({ messageId = '' } = {}) {
      this.$nextTick(() => {
        const messageElement = document.getElementById('message' + messageId);
        if (messageElement) {
          this.isProgrammaticScroll = true;
          messageElement.scrollIntoView({ behavior: 'smooth' });
          this.fetchPreviousMessages();
        } else {
          this.scrollToBottom();
        }
      });
      this.makeMessagesRead();
    },
    addScrollListener() {
      this.conversationPanel = this.$el.querySelector('.conversation-panel');
      this.setScrollParams();
      this.conversationPanel.addEventListener('scroll', this.handleScroll);
      this.$nextTick(() => this.scrollToBottom());
      this.isLoadingPrevious = false;
    },
    removeScrollListener() {
      this.conversationPanel.removeEventListener('scroll', this.handleScroll);
    },
    scrollToBottom() {
      this.isProgrammaticScroll = true;
      let relevantMessages = [];

      // if there are unread messages, scroll to the first unread message
      if (this.unreadMessageCount > 0) {
        // capturing only the unread messages
        relevantMessages =
          this.conversationPanel.querySelectorAll('.message--unread');
      } else {
        // if there are no unread messages, scroll to the last message
        // capturing last message from the messages list
        relevantMessages = Array.from(
          this.conversationPanel.querySelectorAll('.message--read')
        ).slice(-1);
      }

      this.conversationPanel.scrollTop = calculateScrollTop(
        this.conversationPanel.scrollHeight,
        this.$el.scrollHeight,
        relevantMessages
      );
    },
    setScrollParams() {
      this.heightBeforeLoad = this.conversationPanel.scrollHeight;
      this.scrollTopBeforeLoad = this.conversationPanel.scrollTop;
    },

    async fetchPreviousMessages(scrollTop = 0) {
      this.setScrollParams();
      const shouldLoadMoreMessages =
        this.currentChat.dataFetched === true &&
        !this.listLoadingStatus &&
        !this.isLoadingPrevious;

      if (
        scrollTop < 100 &&
        !this.isLoadingPrevious &&
        shouldLoadMoreMessages
      ) {
        this.isLoadingPrevious = true;
        try {
          await this.$store.dispatch('fetchPreviousMessages', {
            conversationId: this.currentChat.id,
            before: this.currentChat.messages[0].id,
          });
          const heightDifference =
            this.conversationPanel.scrollHeight - this.heightBeforeLoad;
          this.conversationPanel.scrollTop =
            this.scrollTopBeforeLoad + heightDifference;
          this.setScrollParams();
        } catch (error) {
          // Ignore Error
        } finally {
          this.isLoadingPrevious = false;
        }
      }
    },

    handleScroll(e) {
      if (this.isProgrammaticScroll) {
        // Reset the flag
        this.isProgrammaticScroll = false;
        this.hasUserScrolled = false;
      } else {
        this.hasUserScrolled = true;
      }
      emitter.emit(BUS_EVENTS.ON_MESSAGE_LIST_SCROLL);
      this.fetchPreviousMessages(e.target.scrollTop);
    },

    makeMessagesRead() {
      if (this.isQaMonitorMode) return;

      this.$store.dispatch('markMessagesRead', { id: this.currentChat.id });
    },
    async handleMessageRetry(message) {
      if (!message) return;
      const payload = useSnakeCase(message);
      await this.$store.dispatch('sendMessageWithData', payload);
    },
    onOpenChatClick() {
      if (this.isAssignedToOtherAgent) {
        this.$refs.takeoverDialog?.open();
      } else {
        this.reopenChat();
      }
    },
    reopenChat() {
      this.$store.dispatch('toggleStatus', {
        conversationId: this.currentChat.id,
        status: 'open',
      });
    },
    async confirmTakeover() {
      try {
        await this.$store.dispatch('assignAgent', {
          conversationId: this.currentChat.id,
          agentId: this.currentUserId,
        });
      } catch (error) {
        // assignment might fail silently
      }
      this.reopenChat();
    },
    onCloseChatClick() {
      this.$refs.closeChatDialog?.open();
    },
    async closeAndArchiveChat() {
      if (this.isClosingUnassignedChat || !this.currentChat?.id) return;

      this.isClosingUnassignedChat = true;
      try {
        await this.$store.dispatch('toggleStatus', {
          conversationId: this.currentChat.id,
          status: wootConstants.STATUS_TYPE.RESOLVED,
          caseCloseType: 'complete',
        });

        const status = String(this.currentChat?.status || '');
        if (status === wootConstants.STATUS_TYPE.RESOLVED) {
          useAlert(this.$t('CONVERSATION.CHANGE_STATUS'));
        } else {
          useAlert(this.$t('CONVERSATION.CHANGE_STATUS_FAILED'));
        }
      } finally {
        this.isClosingUnassignedChat = false;
      }
    },
    async assignConversationToMe() {
      if (this.isAssigningToSelf || !this.currentChat?.id) return;

      this.isAssigningToSelf = true;
      try {
        await this.$store.dispatch('assignAgent', {
          conversationId: this.currentChat.id,
          agentId: this.currentUserId,
        });

        const assigneeId = Number(this.currentChat?.meta?.assignee?.id || 0);
        if (assigneeId === Number(this.currentUserId)) {
          useAlert(this.$t('CONVERSATION.CHANGE_AGENT'));
        } else {
          useAlert(this.$t('CONVERSATION.CHANGE_AGENT_FAILED'));
        }
      } finally {
        this.isAssigningToSelf = false;
      }
    },
    async confirmCloseChat() {
      await this.closeAndArchiveChat();
    },
  },
};
</script>

<template>
  <div
    class="flex flex-col justify-between flex-grow h-full min-w-0 m-0"
    style="background-color: var(--wlab-bg-primary)"
  >
    <Banner
      v-if="!currentChat.can_reply"
      color-scheme="alert"
      class="mx-2 mt-2 overflow-hidden rounded-lg"
      :banner-message="replyWindowBannerMessage"
      :href-link="replyWindowLink"
      :href-link-text="replyWindowLinkText"
    />
    <Banner
      v-else-if="hasDuplicateInstagramInbox"
      color-scheme="alert"
      class="mx-2 mt-2 overflow-hidden rounded-lg"
      :banner-message="$t('CONVERSATION.OLD_INSTAGRAM_INBOX_REPLY_BANNER')"
    />
    <!-- §10.2 Voice calls banner — renders only if voice_calls exist
         on this conversation. Self-fetches; silent when empty so it
         never pushes the first message off-screen in voice-less chats. -->
    <ConversationVoiceCallsBanner
      v-if="currentChat?.id"
      :conversation-id="currentChat.id"
      :account-id="currentAccountId"
    />
    <MessageList
      ref="conversationPanelRef"
      class="conversation-panel flex-shrink flex-grow basis-px flex flex-col overflow-y-auto relative h-full m-0 pb-4"
      :current-user-id="currentUserId"
      :first-unread-id="unReadMessages[0]?.id"
      :is-an-email-channel="isAnEmailChannel"
      :inbox-supports-reply-to="inboxSupportsReplyTo"
      :messages="getMessages"
      @retry="handleMessageRetry"
    >
      <template #beforeAll>
        <transition name="slide-up">
          <!-- eslint-disable-next-line vue/require-toggle-inside-transition -->
          <li
            class="min-h-[4rem] flex flex-shrink-0 flex-grow-0 items-center flex-auto justify-center max-w-full mt-0 mr-0 mb-1 ml-0 relative first:mt-auto last:mb-0"
          >
            <Spinner v-if="shouldShowSpinner" class="text-white" />
          </li>
        </transition>
      </template>
      <template #unreadBadge>
        <li
          v-show="unreadMessageCount != 0"
          class="list-none flex justify-center items-center"
        >
          <span
            class="shadow-lg rounded-full bg-n-brand text-white text-xs font-medium my-2.5 mx-auto px-2.5 py-1.5"
          >
            {{ unreadMessageLabel }}
          </span>
        </li>
      </template>
    </MessageList>
    <div
      v-if="!hideReplyBox"
      class="flex relative flex-col"
      :class="{
        'modal-mask': isPopOutReplyBox,
      }"
      style="background-color: var(--wlab-bg-primary)"
    >
      <div
        v-if="isLiveChatWebWidget && isAnyoneTyping"
        class="absolute flex items-center w-full h-0 -top-7"
      >
        <div
          v-if="typingUsersToMonitor.length"
          class="wlab-typing-monitor mx-auto"
        >
          <div
            v-for="u in typingUsersToMonitor"
            :key="`${u.type}-${u.id}`"
            class="wlab-typing-sneakpeek"
          >
            <div class="wlab-typing-dots" data-test="typing-indicator">
              <span /><span /><span />
            </div>
            <p class="wlab-sneak-peek-text privacy-masker" dir="auto">
              <span>{{ u.name }}:</span>
              <span class="ml-1">{{ u.typingPreview || '…' }}</span>
            </p>
          </div>
        </div>
        <div
          v-else
          class="flex py-2 pr-4 pl-5 shadow-md rounded-full bg-white dark:bg-n-solid-3 text-n-slate-11 text-xs font-semibold my-2.5 mx-auto"
        >
          {{ typingUserNames }}
          <img
            class="w-6 ltr:ml-2 rtl:mr-2"
            src="assets/images/typing.gif"
            alt="Someone is typing"
          />
        </div>
      </div>
      <div v-if="shouldShowUnassignedReplyGate" class="wlab-unassigned-box">
        <p class="wlab-unassigned-box__text">This chat is unassigned.</p>
        <div class="wlab-unassigned-box__actions">
          <button
            class="wlab-unassigned-box__btn wlab-unassigned-box__btn--secondary"
            type="button"
            :disabled="isClosingUnassignedChat"
            @click="closeAndArchiveChat"
          >
            Close &amp; archive
          </button>
          <button
            class="wlab-unassigned-box__btn wlab-unassigned-box__btn--primary"
            type="button"
            data-test-id="assign-chat-btn"
            :disabled="isAssigningToSelf"
            @click="assignConversationToMe"
          >
            <span>{{ $t('CONVERSATION.ASSIGN_TO_ME') }}</span>
          </button>
        </div>
      </div>
      <ReplyBox
        v-else-if="(!isQaMonitorMode || isSuperviseMode) && !isResolved"
        :pop-out-reply-box="isPopOutReplyBox"
        :force-private-note-mode="isSuperviseMode"
        @update:pop-out-reply-box="isPopOutReplyBox = $event"
      />
      <div v-else-if="!isQaMonitorMode && isResolved" class="wlab-archived-box">
        <p class="wlab-archived-box__text">{{ archivedMessage }}</p>
        <button
          class="wlab-archived-box__btn"
          type="button"
          @click="onOpenChatClick"
        >
          Open chat
        </button>
      </div>
    </div>

    <ConfirmActionDialog
      ref="takeoverDialog"
      title="Take over this chat?"
      :description="takeoverDescription"
      confirm-label="Take over"
      cancel-label="Cancel"
      @confirm="confirmTakeover"
    />

    <ConfirmActionDialog
      ref="closeChatDialog"
      title="Close this chat?"
      :description="closeChatDescription"
      confirm-label="Close chat"
      cancel-label="Cancel"
      :confirm-destructive="true"
      @confirm="confirmCloseChat"
    />
  </div>
</template>

<style scoped lang="scss">
.modal-mask {
  @apply fixed;

  &::v-deep {
    .ProseMirror-woot-style {
      @apply max-h-[25rem];
    }

    .reply-box {
      @apply border border-n-weak max-w-[75rem] w-[70%];

      &.is-private {
        @apply dark:border-n-amber-3/30 border-n-amber-12/5;
      }
    }

    .reply-box .reply-box__top {
      @apply relative min-h-[27.5rem];
    }

    .reply-box__top .input {
      @apply min-h-[27.5rem];
    }

    .emoji-dialog {
      @apply absolute ltr:left-auto rtl:right-auto bottom-1;
    }
  }
}

.wlab-archived-box {
  text-align: center;
  align-items: center;
  border-top: 1px solid var(--wlab-border-color, #e5e7eb);
  box-sizing: border-box;
  color: var(--wlab-text-color, #111827);
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  font-size: 16px;
  height: 116px;
  width: 100%;
  justify-content: space-around;
  padding: 20px 9px 17px;
}

.wlab-archived-box__text {
  font-size: 14px;
  line-height: 21px;
}

.wlab-archived-box__btn {
  border: 0;
  background-color: #0059e1;
  color: #fff;
  padding: 8px 16px;
  min-width: 36px;
  height: 36px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
}

.wlab-unassigned-box {
  text-align: center;
  align-items: center;
  border-top: 1px solid var(--wlab-border-color, #e5e7eb);
  box-sizing: border-box;
  color: var(--wlab-text-color, #111827);
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  font-size: 16px;
  height: 116px;
  width: 100%;
  justify-content: space-around;
  padding: 20px 9px 17px;
}

.wlab-unassigned-box__text {
  font-size: 14px;
  line-height: 21px;
}

.wlab-unassigned-box__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 17px;
}

.wlab-unassigned-box__btn {
  align-items: center;
  border: 1px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  font-size: 14px;
  font-weight: 600;
  height: 36px;
  justify-content: center;
  line-height: 20px;
  min-width: 36px;
  padding: 8px 16px;
  transition:
    opacity 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease,
    background-color 0.2s ease;
}

.wlab-unassigned-box__btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.wlab-unassigned-box__btn--secondary {
  background-color: var(--wlab-bg-primary, #ffffff);
  border-color: var(--wlab-border-default, #d1d5db);
  color: var(--wlab-text-primary, #111827);
}

.wlab-unassigned-box__btn--secondary:hover:not(:disabled) {
  background-color: var(--wlab-bg-hover, #f8fafc);
}

.wlab-unassigned-box__btn--primary {
  background-color: #0059e1;
  border-color: #0059e1;
  color: #ffffff;
}

.wlab-unassigned-box__btn--primary:hover:not(:disabled) {
  background-color: #0047b3;
  border-color: #0047b3;
}
</style>
